import { igloohomeApi } from "api/igloohome";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "providers/Layout";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Modal
} from "shared/components";
import { Download01Icon, Add01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { IntegrationDetailHeader } from "views/settings-integration/integrations/shared/IntegrationDetailHeader";
import { LockboxAccountsList } from "views/settings-integration/integrations/shared/LockboxAccountsList";
import { LockboxCredentialsTab } from "views/settings-integration/integrations/shared/LockboxCredentialsTab";
import { ImportLockboxesModal } from "views/settings-integration/integrations/shared/ImportLockboxesModal";
import {
  getVendorAccountErrorCode,
  getVendorAccountErrorMessage,
  VendorAccount
} from "api/lockbox-accounts";
import { toast } from "react-toastify";
import { CALLBACK_REASON_MESSAGES } from "./constants";

const AddIgloohomeAccountModal = ({
  open,
  existingAccounts,
  onClose
}: {
  open: boolean;
  existingAccounts: VendorAccount[];
  onClose: () => void;
}) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");

  const reset = () => {
    setName("");
    setNameError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: startConnect, isPending } = useMutation({
    mutationKey: ["IGLOOHOME", "START_CONNECT"],
    mutationFn: () => igloohomeApi.startConnect(name.trim()),
    onSuccess: ({ authorization_url }) => {
      window.location.assign(authorization_url);
    },
    onError: (err) => {
      const message = getVendorAccountErrorMessage(err);
      if (getVendorAccountErrorCode(err) === "duplicate_name")
        setNameError(message);
      else toast.error(message);
    }
  });

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (existingAccounts.some((account) => account.name === trimmedName)) {
      setNameError("An account with this name already exists");
      return;
    }
    startConnect();
  };

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      panelClassName="w-full max-w-[440px] p-6 space-y-6"
    >
      <h2 className="text-xl font-bold text-content-1000">
        Connect an Igloohome account
      </h2>
      <p className="text-sm text-content-500">
        Name this account, then you&apos;ll be redirected to Igloohome to log in
        and authorize Boom.
      </p>
      <BaseInput
        label="Account name"
        required
        id="igloohome-account-name"
        placeholder="e.g. Main House"
        maxLength={255}
        value={name}
        error={nameError}
        onChange={(value) => {
          setName(value);
          if (nameError) setNameError("");
        }}
      />
      <div className="flex justify-end gap-2">
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          className="w-[125px]"
          onClick={handleClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          className="w-[125px]"
          onClick={handleSubmit}
          loading={isPending}
          disabled={!name.trim()}
        >
          Continue
        </Button>
      </div>
    </Modal>
  );
};

const AddIgloohomeAccountTrigger = ({
  existingAccounts
}: {
  existingAccounts: VendorAccount[];
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        HugeIcon={Add01Icon}
        onClick={() => setOpen(true)}
      >
        Connect account
      </Button>
      <AddIgloohomeAccountModal
        open={open}
        existingAccounts={existingAccounts}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export const IgloohomeView = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [connectedAccountId, setConnectedAccountId] = useState<
    number | undefined
  >(undefined);
  const handledCallbackRef = useRef(false);

  useEffect(() => {
    const callbackStatus = searchParams.get("status");
    if (!callbackStatus || handledCallbackRef.current) return;
    handledCallbackRef.current = true;

    if (callbackStatus === "connected") {
      toast.success("Igloohome account connected successfully");
      queryClient.invalidateQueries({
        queryKey: ["LOCKBOX_ACCOUNTS", "igloohome"]
      });
      const accountId = searchParams.get("account_id");
      setConnectedAccountId(accountId ? Number(accountId) : undefined);
      setImportModalOpen(true);
    } else if (callbackStatus === "denied")
      toast.info("Igloohome connection was cancelled");
    else if (callbackStatus === "error") {
      const reason = searchParams.get("reason") ?? "";
      toast.error(
        CALLBACK_REASON_MESSAGES[reason] ?? "Igloohome connection failed"
      );
    }

    const next = new URLSearchParams(searchParams);
    next.delete("status");
    next.delete("reason");
    next.delete("account_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  const handleImportModalClose = useCallback(() => {
    setImportModalOpen(false);
    setConnectedAccountId(undefined);
  }, []);

  const handleImportSuccess = useCallback(() => {
    handleImportModalClose();
    queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
    navigate("/settings/integration");
  }, [handleImportModalClose, queryClient, navigate]);

  return (
    <Layout
      header={
        <IntegrationDetailHeader
          id={IntegrationId.IGLOOHOME}
          name="Igloohome"
          path="/settings/integration/igloohome"
          actions={
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={Download01Icon}
              onClick={() => setImportModalOpen(true)}
            >
              Import devices
            </Button>
          }
        />
      }
    >
      <LockboxCredentialsTab />
      <LockboxAccountsList
        vendor="igloohome"
        renderAddAccount={({ existingAccounts }) => (
          <AddIgloohomeAccountTrigger existingAccounts={existingAccounts} />
        )}
      />
      <ImportLockboxesModal
        open={importModalOpen}
        onClose={handleImportModalClose}
        onSuccess={handleImportSuccess}
        vendor="igloohome"
        emptyStateDescription="There are no devices paired to this Igloohome account."
        noAccountsDescription="No Igloohome accounts connected. Connect an account before importing devices."
        api={igloohomeApi}
        initialAccountId={connectedAccountId}
      />
    </Layout>
  );
};
